/**
 * RC-2 FIX: Dynamic Package Catalog Context
 * 
 * SINGLE SOURCE OF TRUTH: Backend DB via GET /api/subscription/packages
 * 
 * ARCHITECTURE:
 * - On mount, fetches ALL package data from the backend API
 * - NO hardcoded DEFAULT_PACKAGES — catalog starts empty
 * - NO static fallback — if API fails, catalog stays empty + error state
 * - All consumer components read from this context
 * - Refreshes on demand (e.g., when UpgradeModal opens)
 * 
 * KEY PRINCIPLE:
 * - Backend is canonical. Frontend renders what API returns.
 * - Catalog changes affect NEW subscriptions only.
 * - Existing subscriptions use their frozen snapshots.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const PackageCatalogContext = createContext(null);

// =============================================================================
// TRANSFORM: Maps backend API response to UI shape
// RULE: No data is added or overridden — faithful pass-through with field renames
// =============================================================================

const VISUAL_MAP = {
  'TRIAL':      { color: 'bg-blue-100 text-blue-800 border-blue-200',   badgeColor: 'bg-blue-500',   icon: 'Clock' },
  'STARTER':    { color: 'bg-green-100 text-green-800 border-green-200', badgeColor: 'bg-green-500',  icon: 'Zap' },
  'PRO':        { color: 'bg-purple-100 text-purple-800 border-purple-200', badgeColor: 'bg-purple-500', icon: 'Rocket' },
  'ENTERPRISE': { color: 'bg-amber-100 text-amber-800 border-amber-200', badgeColor: 'bg-amber-500',  icon: 'Building2' },
  'PREMIUM':    { color: 'bg-rose-100 text-rose-800 border-rose-200',   badgeColor: 'bg-rose-500',   icon: 'Crown' },
};
const DEFAULT_VISUAL = { color: 'bg-teal-100 text-teal-800 border-teal-200', badgeColor: 'bg-teal-500', icon: 'Package' };

/** Transform a single backend package object to the shape the UI expects */
const transformApiPackage = (apiPkg) => {
  const id = apiPkg.id;
  const visual = VISUAL_MAP[id] || DEFAULT_VISUAL;

  const nameStr = typeof apiPkg.name === 'object' ? (apiPkg.name?.en || Object.values(apiPkg.name)[0] || '') : (apiPkg.name || '');
  const descStr = typeof apiPkg.description === 'object' ? (apiPkg.description?.en || Object.values(apiPkg.description)[0] || '') : (apiPkg.description || '');

  return {
    id,
    docId: apiPkg.doc_id || null,
    version: apiPkg.version || 1,
    name: apiPkg.name,
    displayName: nameStr,
    description: descStr,
    ...visual,
    pricing: {
      amount: apiPkg.amount,
      currency: (apiPkg.currency || 'eur').toUpperCase(),
      interval: apiPkg.billing_interval || null,
      customPricing: apiPkg.requires_contact_sales || false,
    },
    duration: {
      type: apiPkg.trial_days > 0 && apiPkg.amount === 0 ? 'trial' : 'subscription',
      defaultDays: apiPkg.trial_days || null,
      billingCycle: apiPkg.billing_interval || 'monthly',
    },
    limits: apiPkg.limits || {},
    features: apiPkg.features || {},
    requires_contact_sales: apiPkg.requires_contact_sales || false,
    upgradePrompt: apiPkg.requires_contact_sales ? null : 'Upgrade to unlock more features',
    public: apiPkg.is_public !== false,
    active: apiPkg.status !== 'inactive',
    sortOrder: apiPkg.upgrade_order ?? 0,
    upgrade_order: apiPkg.upgrade_order ?? 0,
    stripe: apiPkg.stripe || { stripe_product_id: null, active_price_id: null },
  };
};

// =============================================================================
// PROVIDER
// =============================================================================

export const PackageCatalogProvider = ({ children }) => {
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  // PKG-CATALOG-ADMIN-VISIBILITY-01: 'public' (marketing, public-only) vs
  // 'admin' (all packages incl. hidden/inactive). Set by the admin catalog page.
  const scopeRef = useRef('public');

  const setCatalogScope = useCallback((scope) => {
    scopeRef.current = scope === 'admin' ? 'admin' : 'public';
  }, []);

  const fetchFromBackend = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const path = scopeRef.current === 'admin'
        ? '/admin/subscription/packages'
        : '/subscription/packages';
      const res = await api.get(path);
      const packages = res.data?.packages || [];

      const transformed = {};
      packages.forEach(p => {
        const pkg = transformApiPackage(p);
        transformed[pkg.id] = pkg;
      });

      setCatalog(transformed);
      setLastSync(new Date().toISOString());
      return transformed;
    } catch (err) {
      console.error('[PackageCatalog] Failed to fetch packages:', err.message);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFromBackend();
  }, [fetchFromBackend]);

  const refreshCatalog = useCallback(async () => {
    return await fetchFromBackend();
  }, [fetchFromBackend]);

  // =============================================================================
  // READ OPERATIONS — all derived from catalog state (which comes from API)
  // =============================================================================

  const getAllPackages = useCallback(() => {
    return Object.values(catalog).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [catalog]);

  const getAvailablePackages = useCallback(() => {
    return Object.values(catalog)
      .filter(pkg => pkg.public === true && pkg.active === true)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [catalog]);

  const getUpgradeablePackages = useCallback(() => {
    return getAvailablePackages().filter(pkg => pkg.id !== 'TRIAL');
  }, [getAvailablePackages]);

  const getPackageById = useCallback((packageId) => {
    return catalog[packageId] || null;
  }, [catalog]);

  /**
   * Get upgrade path: next tier after currentPackageId based on sortOrder
   */
  const getUpgradePath = useCallback((currentPackageId) => {
    const available = getAvailablePackages();
    const currentPkg = catalog[currentPackageId];
    if (!currentPkg) return available[0] || null;

    const currentOrder = currentPkg.sortOrder ?? 0;
    const nextTier = available.find(pkg => (pkg.sortOrder ?? 0) > currentOrder);
    return nextTier || null;
  }, [catalog, getAvailablePackages]);

  // =============================================================================
  // WRITE OPERATIONS — All API-backed. DB is the single source of truth.
  // Every write calls the backend first. On success, refreshCatalog() reloads
  // from DB. On failure, local state is NOT mutated — error is thrown.
  // =============================================================================

  /** Map frontend form data → backend PackageRequest format.
   *  PKG-LIMIT-FIDELITY-01: an unsupplied quota is sent as `null` so the
   *  canonical package writer (and `PackageLimits`) owns every default. No
   *  literal business value is introduced on the client. */
  const toApiPayload = useCallback((data) => {
    const intervalMap = { month: 'monthly', year: 'yearly', quarterly: 'quarterly' };
    const limit = (value) => (value === '' || value === null || value === undefined ? null : parseInt(value, 10));
    const limits = data.limits || {};
    return {
      name: data.displayName || data.name || '',
      description: data.description || '',
      price: parseFloat(data.pricing?.amount ?? data.price ?? 0),
      currency: (data.pricing?.currency || 'EUR').toUpperCase(),
      billingInterval: intervalMap[data.pricing?.interval] || data.pricing?.interval || 'monthly',
      leadsPerMonth: limit(limits.leads_per_month),
      maxUsers: limit(limits.max_users ?? limits.sales_reps),
      salesReps: limit(limits.sales_reps ?? limits.max_users),
      offersPerMonth: limit(limits.offers_per_month),
      quotesPerMonth: limit(limits.quotes_monthly_max),
      activeThreadsMax: limit(limits.active_threads_max),
      campaignsActiveMax: limit(limits.campaigns_active_max),
      campaignsCreateMonthlyMax: limit(limits.campaigns_create_monthly_max),
      features: data.features || {},
      isActive: data.active !== false,
      isPublic: data.public !== false,
      requiresContactSales: data.requires_contact_sales || false,
      upgradeOrder: data.sortOrder ?? 99,
    };
  }, []);

  const createPackage = useCallback(async (packageData) => {
    const payload = toApiPayload(packageData);
    payload.packageKey = packageData.id || '';
    const res = await api.post('/packages', payload);
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to create package');
    await refreshCatalog();
    return res.data.package;
  }, [toApiPayload, refreshCatalog]);

  const updatePackage = useCallback(async (packageId, updates) => {
    const existing = catalog[packageId];
    const docId = existing?.docId || packageId;
    const merged = { ...existing, ...updates };
    const payload = toApiPayload(merged);
    const res = await api.put(`/packages/${docId}`, payload);
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to update package');
    await refreshCatalog();
    return res.data.package;
  }, [catalog, toApiPayload, refreshCatalog]);

  const activatePackage = useCallback(async (id) => {
    const existing = catalog[id];
    const docId = existing?.docId || id;
    const res = await api.put(`/packages/${docId}/status`, { status: 'active' });
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to activate package');
    await refreshCatalog();
  }, [catalog, refreshCatalog]);

  const deactivatePackage = useCallback(async (id) => {
    const existing = catalog[id];
    const docId = existing?.docId || id;
    const res = await api.put(`/packages/${docId}/status`, { status: 'inactive' });
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to deactivate package');
    await refreshCatalog();
  }, [catalog, refreshCatalog]);

  const makePackagePublic = useCallback(async (id) => {
    const existing = catalog[id];
    const docId = existing?.docId || id;
    const res = await api.put(`/packages/${docId}/visibility`, { is_public: true });
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to update package');
    await refreshCatalog();
  }, [catalog, refreshCatalog]);

  const makePackagePrivate = useCallback(async (id) => {
    const existing = catalog[id];
    const docId = existing?.docId || id;
    const res = await api.put(`/packages/${docId}/visibility`, { is_public: false });
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to update package');
    await refreshCatalog();
  }, [catalog, refreshCatalog]);

  const deletePackage = useCallback(async (packageId) => {
    const existing = catalog[packageId];
    const docId = existing?.docId || packageId;
    const res = await api.delete(`/packages/${docId}`);
    if (!res.data?.success) throw new Error(res.data?.error || 'Failed to delete package');
    await refreshCatalog();
  }, [catalog, refreshCatalog]);

  const resetToDefaults = useCallback(() => { fetchFromBackend(); }, [fetchFromBackend]);

  const value = {
    catalog, loading, lastSync, error,
    getAllPackages, getAvailablePackages, getUpgradeablePackages,
    getPackageById, getUpgradePath, refreshCatalog,
    createPackage, updatePackage, activatePackage, deactivatePackage,
    makePackagePublic, makePackagePrivate, deletePackage, resetToDefaults,
    setCatalogScope,
  };

  return (
    <PackageCatalogContext.Provider value={value}>
      {children}
    </PackageCatalogContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

export const usePackageCatalog = () => {
  const context = useContext(PackageCatalogContext);
  if (!context) throw new Error('usePackageCatalog must be used within PackageCatalogProvider');
  return context;
};

// Same canonical catalog, for screens that may render outside the provider.
// Two admin pages used to call `usePackageCatalog()` inside a try/catch IIFE to
// survive a missing provider, which is a hook inside a callback
// (react-hooks/rules-of-hooks): the subscription was registered on some renders
// and not others. This returns the SAME context when a provider is present and
// an empty catalog when it is not — never a hardcoded package list.
const EMPTY_CATALOG = Object.freeze({
  getAllPackages: () => [],
  getAvailablePackages: () => [],
  getUpgradeablePackages: () => [],
  refreshCatalog: () => {},
  lastSync: null,
  loading: false,
  error: null,
});

export const usePackageCatalogOptional = () => {
  const context = useContext(PackageCatalogContext);
  return context || EMPTY_CATALOG;
};

export default PackageCatalogContext;
